import type { ResourcePickerCategory, ResourcePickerItem } from '../types';
import { pickRecent } from './mention-recents';

/**
 * How many items of one category the idle list shows before it stops. Opening
 * the picker on an org with fifty agents should not mean scrolling past forty
 * of them to notice that datasets exist.
 */
export const IDLE_PER_CATEGORY_CAP = 5;

/** A category the cap cut short, with the numbers the hint quotes. */
export type TruncatedCategory = {
  category: ResourcePickerCategory;
  shown: number;
  total: number;
};

export type IdleView<T> = {
  /** Most recently inserted first. Empty until something has been picked. */
  recent: ResourcePickerItem<T>[];
  /** The remaining items, capped per category, in the caller's order. */
  byCategory: ResourcePickerItem<T>[];
  /** Categories the cap cut short, so the UI can say how much is hidden. */
  truncated: TruncatedCategory[];
};

/**
 * Splits the no-query view into a recents band and a capped sample of
 * everything else.
 *
 * The unfiltered list is ordered by category and then however the caller
 * supplied it, so the first screen is whichever category sorts first rather
 * than anything the user is likely to want. Recents fix the common case;
 * the cap keeps the rest browsable.
 */
export const buildIdleView = <T>(
  items: ResourcePickerItem<T>[],
  recentIds: string[],
  perCategoryCap: number = IDLE_PER_CATEGORY_CAP,
  /**
   * Categories that render as their own collapsed summary rather than one row
   * per item, plus any the user has expanded via "Show all".
   */
  exemptCategories: ResourcePickerCategory[] = []
): IdleView<T> => {
  const exempt = new Set(exemptCategories);
  const recent = pickRecent(items, recentIds);
  const recentIdSet = new Set(recent.map((item) => item.id));

  const seen = new Map<ResourcePickerCategory, number>();
  const total = new Map<ResourcePickerCategory, number>();
  const byCategory: ResourcePickerItem<T>[] = [];

  for (const item of items) {
    // Counted before the recent check: the hint quotes how many of this type
    // exist, and a recent entry is still one of them. Quoting the post-recents
    // remainder would read as one fewer agent than the org actually has.
    total.set(item.category, (total.get(item.category) ?? 0) + 1);

    // A recent entry still belongs in its category group; showing it twice in
    // one short list reads as a duplicate rather than as a shortcut.
    if (recentIdSet.has(item.id)) {
      continue;
    }
    if (exempt.has(item.category)) {
      byCategory.push(item);
      continue;
    }
    const count = seen.get(item.category) ?? 0;
    if (count >= perCategoryCap) {
      continue;
    }
    seen.set(item.category, count + 1);
    byCategory.push(item);
  }

  const truncated: TruncatedCategory[] = [];
  for (const [category, totalCount] of total) {
    const shown = seen.get(category) ?? 0;
    if (!exempt.has(category) && shown < totalCount) {
      truncated.push({ category, shown, total: totalCount });
    }
  }

  return { recent, byCategory, truncated };
};
